import { createContext, useContext, useState, ReactNode } from "react"
import { Modal, Pressable, PressableProps, View } from "react-native"
import { cn } from "@src/utils"
import { Text } from "./typography"

interface SelectContextValue {
  value?: string
  onChange: (value: string) => void
  open: boolean
  setOpen: (open: boolean) => void
  label?: string
  setLabel: (label: string) => void
}

const SelectContext = createContext<SelectContextValue>({
  value: undefined,
  onChange: () => {},
  open: false,
  setOpen: () => {},
  label: undefined,
  setLabel: () => {},
})

interface SelectProps {
  value?: string
  onValueChange: (value: string) => void
  children: ReactNode
}

export function Select({ value, onValueChange, children }: SelectProps) {
  const [open, setOpen] = useState(false)
  const [label, setLabel] = useState<string | undefined>(undefined)

  return <SelectContext.Provider value={{ value, onChange: onValueChange, open, setOpen, label, setLabel }}>{children}</SelectContext.Provider>
}

interface SelectTriggerProps extends Omit<PressableProps, "children"> {
  className?: string
  children: ReactNode
}

export function SelectTrigger({ className, children, ...props }: SelectTriggerProps) {
  const { setOpen } = useContext(SelectContext)
  return (
    <Pressable
      className={cn("flex h-11 w-full flex-row items-center justify-between rounded-md border border-gray-200 bg-transparent px-3 py-2 text-sm", className)}
      onPress={() => setOpen(true)}
      {...props}
    >
      {children}
    </Pressable>
  )
}

interface SelectValueProps {
  placeholder?: string
  className?: string
}

export function SelectValue({ placeholder, className }: SelectValueProps) {
  const { value, label } = useContext(SelectContext)
  return (
    <Text variant="small" className={cn("text-gray-900", !value && "text-gray-500", className)}>
      {label || value || placeholder || ""}
    </Text>
  )
}

interface SelectContentProps {
  className?: string
  children: ReactNode
}

export function SelectContent({ className, children }: SelectContentProps) {
  const { open, setOpen } = useContext(SelectContext)

  return (
    <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
      <Pressable className="absolute inset-0 bg-black/60" onPress={() => setOpen(false)} />
      <View className="flex-1 justify-center px-4 py-8" style={{ pointerEvents: "box-none" }}>
        <View className={cn("bg-white rounded-lg shadow-xl overflow-hidden max-h-[70%] w-[90%] max-w-sm mx-auto", className)}>
          <View className="py-2">{children}</View>
        </View>
      </View>
    </Modal>
  )
}

interface SelectItemProps {
  value: string
  label?: string
  className?: string
  children: ReactNode
}

export function SelectItem({ value, label, className, children }: SelectItemProps) {
  const { setOpen, onChange, value: selectedValue, setLabel } = useContext(SelectContext)

  const isSelected = selectedValue === value

  return (
    <Pressable
      className={cn("relative flex flex-row items-center rounded-sm px-2 py-2.5 mx-1 active:bg-gray-100", isSelected && "bg-blue-50", className)}
      onPress={() => {
        onChange(value)
        if (label || typeof children === "string") {
          setLabel(label || (children as string))
        }
        setOpen(false)
      }}
    >
      <View className="flex-1">{children}</View>
      {isSelected && <Text className="text-blue-600 ml-2">✓</Text>}
    </Pressable>
  )
}
